import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { di } from '../../../../state/inversify.config';
import { AssessmentQueryParamService } from '../assessment.queryParam.service';
import { AssessmentModules } from './modules';
import { BalanceDoubleLegStance } from './modules/balanceDoubleLegStance/balanceDoubleLegStance.smart';
import { BalanceSingleLegStance } from './modules/balanceSingleLegStance/balanceSingleLegStance.smart';
import { BalanceTandemGait } from './modules/balanceTandemGait/balanceTandemGait.smart';
import { BalanceTandemStance } from './modules/balanceTandemStance/balanceTandemStance.smart';
import { DelayedRecall } from './modules/delayedRecall/delayedRecall.smart';
import { DigitsBackwards } from './modules/digitsBackwards/digitsBackwards.smart';
import { GlasgowComaScale } from './modules/glasgowComaScale/glasgowComaScale.smart';
import { ImmediateMemory } from './modules/immediateMemory/immediateMemory.smart';
import { MonthsInReverse } from './modules/monthsInReverse/monthsInReverse.smart';
import { NeckExam } from './modules/neckExam/neckExam.smart';
import { NeurologicalExam } from './modules/neurologicalExam/neurologicalExam.smart';
import { SymptomsChecklist } from './modules/symptomsChecklist/symptomsChecklist.smart';
import { TreatmentPlan } from './modules/treatmentPlan/treatmentPlan.smart';
import { VOMS } from './modules/voms/voms.smart';
import { TakeAssessmentDomain } from './takeAssessmentDomainService';
import { getRouteByAccountType } from '../../../../routes/getRouteByAccountType';
import { AuthService } from '../../../../state/helperServices/auth/auth.service';
import { UserNotificationType } from '../../../../components/userNotification/userNotificationType.enum';
import { UserNotificationContext } from '../../../../components/userNotification/userNotificationContext';
import { Stepper } from '../../../../components/stepper/stepper';

export function TakeAssessment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addUserNotification, removeNotification } = useContext(
    UserNotificationContext,
  );

  const takeAssessmentDomain =
    di.get<TakeAssessmentDomain>(TakeAssessmentDomain);
  const currentModule = takeAssessmentDomain.useModuleSelector();

  // TODO: move into domain service
  const assessmentQueryParamService = di.get<AssessmentQueryParamService>(
    AssessmentQueryParamService,
  );
  const queryParams = assessmentQueryParamService.useSelectMergedQuery();

  // TODO: move into a domainService and only return accountType or even better find a way so that you don't have to send down the accountType
  const authService = di.get<AuthService>(AuthService);
  const loggedInUser = authService.useLoggedInUserSelector();

  useEffect(() => {
    takeAssessmentDomain.dispatchActiveStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams?.activeStep]);

  const [showValidationWarning, setShowValidationWarning] = useState(
    takeAssessmentDomain.shouldShowValidationWarning,
  );

  const showValidationWarningCallback = (value: boolean) => {
    setShowValidationWarning(value);
  };

  takeAssessmentDomain.setShowValidationWarningCallback(
    showValidationWarningCallback,
  );

  const navigateToSummary = () => {
    takeAssessmentDomain.dispatchResetStepper();
    assessmentQueryParamService.dispatchQueryParams({
      activeStep: 1,
    });
    navigate(
      `/${getRouteByAccountType(
        loggedInUser?.accountType,
      )}/assessment/assessment-summary`,
    );
  };

  useEffect(() => {
    // scroll to top of the page for each assessment module
    window.scrollTo(0, 0);
  }, [currentModule]);

  useEffect(() => {
    setShowValidationWarning(takeAssessmentDomain.shouldShowValidationWarning);

    if (takeAssessmentDomain.shouldShowValidationWarning) {
      addUserNotification(
        UserNotificationType.WARNING,
        t('assessment.ID_PLEASE_ANSWER_ALL_QUESTIONS'),
        UserNotificationType.WARNING,
      );
    } else {
      removeNotification();
    }
    setShowValidationWarning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeAssessmentDomain.shouldShowValidationWarning, showValidationWarning]);

  return (
    <>
      {currentModule === AssessmentModules.GlasgowComaScale && (
        <GlasgowComaScale domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.ImmediateMemory && (
        <ImmediateMemory domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.SymptomChecklist && (
        <SymptomsChecklist domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.DigitsBackwards && (
        <DigitsBackwards domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.MonthsInReverse && (
        <MonthsInReverse domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.BalanceTandemGait && (
        <BalanceTandemGait domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.BalanceDoubleLegStance && (
        <BalanceDoubleLegStance domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.BalanceSingleLegStance && (
        <BalanceSingleLegStance domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.BalanceTandemStance && (
        <BalanceTandemStance domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.DelayedRecall && (
        <DelayedRecall domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.NeckExam && (
        <NeckExam domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.NeurologicalExam && (
        <NeurologicalExam domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.VOMS && (
        <VOMS domainService={takeAssessmentDomain} />
      )}
      {currentModule === AssessmentModules.TreatmentPlan && (
        <TreatmentPlan domainService={takeAssessmentDomain} />
      )}

      <Stepper
        stepperDomain={takeAssessmentDomain}
        totalSteps={takeAssessmentDomain.totalAssessmentSteps()}
        customNext={takeAssessmentDomain.isLastAssessmentScreen()}
        customNextText={t('assessment.VIEW_SUMMARY')}
        customNextAction={navigateToSummary}
      />
    </>
  );
}
